import {BudgetComponentType, FinancialEntityType, GetChartOfAccountsQuery, MathOperationType} from "../../__generated__/generated_types";

type CoaRecord = GetChartOfAccountsQuery["listChartOfAccounts"]["records"][number];

export interface ComponentOp {
    sourceComponentId: string;
    operation: MathOperationType;
}

export interface FinancialEntity {
    id: string;
    name: string;
    number: string;
    parentId: string | undefined;
    budgetComponentType: BudgetComponentType;
    // Indicates if this financial value when
    // rolled up into a component is negated.
    budgetComponentNegate?: boolean;
    componentOps?: ComponentOp[];
    type: FinancialEntityType;
    children: FinancialEntity[];
    order: number;
}


export type TChartOfAccountsData = {
    chartOfAccounts: FinancialEntity[];
    chartOfAccountsFlat: FinancialEntity[];
    parents: Record<string, FinancialEntity>;
    isIncome: Record<string, boolean>;
};

export function buildChartOfAccountsData(records: GetChartOfAccountsQuery["listChartOfAccounts"]["records"],
    budgetComponentMappings: GetChartOfAccountsQuery["listChartOfAccounts"]["budgetComponentMappings"]
): TChartOfAccountsData {
    const componentRecords = records.filter(row => row.type == FinancialEntityType.Component);
    const allRecordsById = records.toIdMap("id");
    const componentGroups = Object.entries(budgetComponentMappings.groupBy(row => row.componentId)).sortBy(([componentId]) => componentRecords.find(rec => rec.id == componentId)?.order ?? 0);
    const chartOfAccounts = [] as FinancialEntity[];
    const chartOfAccountFlat = [] as FinancialEntity[];
    const isIncome: Record<string, boolean> = {};

    const parents: Record<string, FinancialEntity> = {};

    componentGroups.forEach(([componentId, group]) => {
        const componentRaw = componentRecords.find(rec => rec.id == componentId);
        if(!componentRaw) {
            // should really never happen
            return;
        }
        const component: {budgetComponentType: BudgetComponentType, id: string, name: string, order: number} = {
            budgetComponentType: componentRaw.budgetComponentType ?? BudgetComponentType.Undefined,
            id: componentId,
            name: componentRaw.glName,
            order: componentRaw.order
        };
        const componentFE: FinancialEntity = {
            id: component.id,
            name: component.name,
            number: "",
            order: component.order,
            componentOps: group.filter(row => row.sourceComponentId != undefined && row.sourceComponentId != null
                || row.sourceAccountId != undefined && row.sourceAccountId != null)
                .map(row => ({
                    sourceComponentId: row.sourceComponentId ?? row.sourceAccountId ?? "", // coalesce never hit because of filter before map
                    operation: row.negate ? MathOperationType.Sub : MathOperationType.Add
                })),
            budgetComponentType: component.budgetComponentType,
            type: FinancialEntityType.Component,
            parentId: undefined,
            children: []
        };

        parents[component.id] = componentFE;
        isIncome[component.id] = component.budgetComponentType == "INCOME";
        chartOfAccounts.push(componentFE);
        chartOfAccountFlat.push(componentFE);


        function getChildrenAccounts(
            parentAccount: Pick<CoaRecord, 'id' | 'glName' | 'glNumber' | 'order' | 'type' | 'parentIds'>,
            allRecords: CoaRecord[],
            parentFE: FinancialEntity,
            isIncomeComponent: boolean,
            budgetComponentNegate: boolean
        ): void {
            const children = allRecords.filter(rec => rec.parentIds.lastElement === parentAccount.id);
            children.sortBy(row => row.order).forEach(child => {
                const childAccountFE: FinancialEntity = {
                    id: child.id,
                    name: child.glName,
                    number: child.glNumber ?? "",
                    order: child.order,
                    budgetComponentType: component.budgetComponentType,
                    budgetComponentNegate: budgetComponentNegate,
                    type: child.type,
                    parentId: parentAccount.id,
                    children: []
                };

                isIncome[childAccountFE.id] = isIncomeComponent;
                parents[childAccountFE.id] = childAccountFE;
                parentFE.children.push(childAccountFE);
                chartOfAccountFlat.push(childAccountFE);

                if(childAccountFE.type != FinancialEntityType.Account) {
                    getChildrenAccounts(child, allRecords, childAccountFE, isIncomeComponent, budgetComponentNegate);
                }
            });
        }

        for(const accountIdRow of
            group.filter(row => row.sourceAccountId != undefined && row.sourceAccountId != null)
                .sortBy(row => allRecordsById[row.sourceAccountId ?? ""]?.order ?? 0)) {

            const accountRow = allRecordsById[accountIdRow.sourceAccountId ?? ""];
            if(!accountRow) {
                continue;
            }

            const accountFE: FinancialEntity = {
                id: accountRow.id,
                name: accountRow.glName,
                number: accountRow.glNumber ?? "",
                order: accountRow.order,
                budgetComponentType: component.budgetComponentType,
                budgetComponentNegate: accountRow.negate,
                type: accountRow.type,
                parentId: accountIdRow.componentId,
                children: []
            };

            isIncome[accountFE.id] = component.budgetComponentType == "INCOME";
            parents[accountFE.id] = accountFE;
            componentFE.children.push(accountFE);
            chartOfAccountFlat.push(accountFE);

            getChildrenAccounts(
                accountRow,
                records,
                accountFE,
                component.budgetComponentType == "INCOME",
                accountRow.negate
            );

        }
    });

    return {
        chartOfAccounts: chartOfAccounts.sortBy(entity => entity.order),
        chartOfAccountsFlat: chartOfAccountFlat.sortBy(entity => entity.order),
        parents: parents,
        isIncome: isIncome
    };
}